import * as React from 'react';

import { useLockFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { useSpecialOfferValue } from 'context';
import { createSpecialOffer, updateSpecialOffer } from 'services';
import type { SelectOption, TreeSelectSource } from 'types';
import { normalizeToString, PATHNAME } from 'utils';

import { SpecialOfferFormAction } from './action';
import { SpecialOfferFormContent } from './content';

const {
  LOCAL: {
    SPECIAL_OFFERS: { BASE_URL: SPECIAL_OFFERS_BASE_URL },
  },
} = PATHNAME;

type FieldType = Pick<
  ReturnType<typeof useSpecialOfferValue>,
  'title' | 'percent'
> &
  Partial<
    Record<'products' | 'brands', SelectOption[]> &
      Record<'categories', TreeSelectSource[]>
  >;

const SpecialOfferForm = () => {
  const [form] = Form.useForm<FieldType>();
  const { id = '', duration, defaultTitle, mode } = useSpecialOfferValue();
  const navigate = useNavigate();

  const onFinish = useLockFn(
    async ({
      title = '',
      percent = 0,
      products = [],
      brands = [],
      categories = [],
    }: FieldType) => {
      const isUpdatedMode = mode === 'Update';

      const params: Parameters<typeof updateSpecialOffer>[0] = {
        id,
      };
      const body: Parameters<typeof updateSpecialOffer>[1] = {
        title,
        percent,
        duration: duration!,
        productsIds: products.map(({ value }) => normalizeToString(value)),
        brandsIds: brands.map(({ value }) => normalizeToString(value)),
        categoriesIds: categories.map(({ value }) => normalizeToString(value)),
      };

      (isUpdatedMode
        ? updateSpecialOffer(params, body)
        : createSpecialOffer(body)
      )
        .then(() => {
          notification.success({
            message: `تخفیف ویژه ${isUpdatedMode ? defaultTitle : title} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
          });
          navigate(SPECIAL_OFFERS_BASE_URL);
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  return (
    <PrimitiveForm form={form} onFinish={onFinish}>
      <Flex
        className='*:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:basis-[70%] xl:last:*:basis-[30%]'
        gap='large'
      >
        <SpecialOfferFormContent />
        <SpecialOfferFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { SpecialOfferForm };
